// wallet.js - Phantasma Link (non-custodial) wallet adapter.
//
// Verified against phantasma-sdk-ts@0.7.0. Phantasma Link talks to the user's
// wallet (Poltergeist desktop / Ecto) over a local WebSocket bridge; the wallet
// holds the keys and does all signing, so this dApp never sees a private key.
//
// We import the PhantasmaLink class via its narrow subpath to avoid pulling the
// package's Node-only Ledger barrel into the browser bundle.
import { PhantasmaLink } from 'phantasma-sdk-ts/core/link/phantasmaLink';

const DAPP_ID = 'specky-to-the-moon';
const LINK_VERSION = 4;
const PLATFORM = 'phantasma';
const PROVIDER = 'poltergeist'; // Ecto also speaks the Link protocol

let link = null;

// Convert a LinkAccount into a small UI-friendly shape. Balance amounts are raw
// integer strings in the token's smallest unit; scale by decimals for display.
const toAccount = (acc) => {
  const balances = {};
  (acc?.balances || []).forEach((b) => {
    const scaled = Number(b.amount) / Math.pow(10, b.decimals || 0);
    balances[b.symbol] = Number.isFinite(scaled) ? scaled : 0;
  });
  return { address: acc?.address || '', name: acc?.name || '', balances };
};

// Open the wallet connection and resolve with the connected account.
export const connectWallet = () =>
  new Promise((resolve, reject) => {
    try {
      link = new PhantasmaLink(DAPP_ID, false);
      link.login(
        (success) => {
          if (success && link.account) resolve(toAccount(link.account));
          else reject(new Error('Wallet login was rejected.'));
        },
        (message) => reject(new Error(message || 'Wallet connection failed.')),
        LINK_VERSION,
        PLATFORM,
        PROVIDER
      );
    } catch (e) {
      reject(e);
    }
  });

// Sign a server-issued challenge string to prove ownership of the address.
// (The backend will verify this signature when we add accounts/leaderboard.)
export const signLoginChallenge = (challenge) =>
  new Promise((resolve, reject) => {
    if (!link || !link.account) {
      reject(new Error('Connect a wallet first.'));
      return;
    }
    link.signData(
      challenge,
      (signature) => resolve({ address: link.account.address, challenge, signature }),
      (message) => reject(new Error(message || 'Signing failed.'))
    );
  });

export const disconnectWallet = () => {
  if (link) {
    try { link.disconnect(true); } catch (e) { /* ignore */ }
    link = null;
  }
};

export const isConnected = () => !!(link && link.account);
